import React from 'react';
import styled from 'styled-components';

import {
  FaFacebookF,
  FaInstagram,
  FaTiktok,
  FaYoutube,
  FaWhatsapp,
  FaTelegram,
  FaXTwitter,
  FaLinkedinIn,
  FaGlobe,
  FaPhone,
  FaLocationDot,
  FaEnvelope,
} from 'react-icons/fa6';

const DEFAULT_BRAND_PRIMARY_TEXT = 'KEBAB';
const DEFAULT_BRAND_ACCENT_TEXT = 'HOUSE';

const DEFAULT_DESCRIPTION =
  'Kebab House è il luogo dove il gusto incontra la qualità.\n\n' +
  'Piatti preparati ogni giorno con ingredienti freschi e selezionati.';

const DEFAULT_HOURS_TITLE = 'ORARI';

const DEFAULT_HOURS_TEXT = `Lunedì - Domenica

11:00 - 15:00 / 17:00 - 23:00

Aperto tutti i giorni`;

const DEFAULT_CONTACTS_TITLE = 'CONTATTI';
const DEFAULT_CONTACT_PHONE = '+39 123 456 7890';
const DEFAULT_CONTACT_ADDRESS = 'Via Roma 123, 20100 Milano';
const DEFAULT_CONTACT_EMAIL = 'info@kebabhouse.it';

const DEFAULT_VAT_TITLE = 'PARTITA IVA';
const DEFAULT_VAT_NUMBER = 'IT12345678901';

const DEFAULT_COPYRIGHT_TEXT = '© {year} Kebab House. Tutti i diritti riservati.';

const PRIVACY_HASH = '#privacy';
const TERMS_HASH = '#terms';
const COOKIE_POLICY_HASH = '#cookie-policy';

const DEFAULT_PRIVACY_LABEL = 'Privacy Policy';
const DEFAULT_PRIVACY_URL = PRIVACY_HASH;
const DEFAULT_SHOW_PRIVACY = true;

const DEFAULT_TERMS_LABEL = 'Termini e Condizioni';
const DEFAULT_TERMS_URL = TERMS_HASH;
const DEFAULT_SHOW_TERMS = true;

const DEFAULT_COOKIE_POLICY_LABEL = 'Cookie Policy';
const DEFAULT_MANAGE_COOKIES_LABEL = 'Gestisci cookie';
const DEFAULT_SHOW_MANAGE_COOKIES = true;
const DEFAULT_COOKIE_BANNER_ACTIVE = true;

const DEFAULT_DEVELOPER_TEXT = 'Sito realizzato da';
const DEFAULT_DEVELOPER_NAME = 'Moloman';
const DEFAULT_DEVELOPER_URL = 'https://moloman.it';

const DEFAULT_BACKGROUND_COLOR = '#111111';
const DEFAULT_TEXT_COLOR = '#c7c7c7';
const DEFAULT_TITLE_COLOR = '#ffffff';
const DEFAULT_ACCENT_COLOR = '#df2025';
const DEFAULT_LINK_COLOR = '#ffffff';
const DEFAULT_SEPARATOR_COLOR = '#333333';

const HEX_COLOR_PATTERN = /^#[0-9A-Fa-f]{6}$/;

const LEGAL_DOCUMENT_TYPES = {
  privacy: 'privacy',
  terms: 'terms',
  cookiePolicy: 'cookie_policy',
};

const DEFAULT_SOCIAL_LINKS = [
  {
    id: 'fallback-facebook',
    name: 'Facebook',
    icon: 'facebook',
    url: '#',
  },
  {
    id: 'fallback-instagram',
    name: 'Instagram',
    icon: 'instagram',
    url: '#',
  },
  {
    id: 'fallback-tiktok',
    name: 'TikTok',
    icon: 'tiktok',
    url: '#',
  },
];

const SOCIAL_ICON_COMPONENTS = {
  facebook: FaFacebookF,
  instagram: FaInstagram,
  tiktok: FaTiktok,
  youtube: FaYoutube,
  whatsapp: FaWhatsapp,
  telegram: FaTelegram,
  x_twitter: FaXTwitter,
  linkedin: FaLinkedinIn,
  website: FaGlobe,
};

const SOCIAL_ICON_LABELS = {
  facebook: 'Facebook',
  instagram: 'Instagram',
  tiktok: 'TikTok',
  youtube: 'YouTube',
  whatsapp: 'WhatsApp',
  telegram: 'Telegram',
  x_twitter: 'X / Twitter',
  linkedin: 'LinkedIn',
  website: 'Sito web',
};

const FooterWrapper = styled.footer`
  width: 100%;

  padding: 35px 0 15px;

  background: ${({ $backgroundColor }) => $backgroundColor};

  color: ${({ $textColor }) => $textColor};
`;

const FooterContainer = styled.div`
  width: min(100% - 80px, 1400px);

  margin: 0 auto;

  @media (max-width: 900px) {
    width: min(100% - 40px, 1400px);
  }

  @media (max-width: 600px) {
    width: min(100% - 30px, 1400px);
  }
`;

const FooterGrid = styled.div`
  display: grid;

  grid-template-columns: 1.3fr 1fr 1.2fr 0.8fr;

  gap: 35px;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 550px) {
    grid-template-columns: 1fr;
  }
`;

const FooterColumn = styled.div`
  min-width: 0;

  h3 {
    margin: 0 0 18px;

    color: ${({ $titleColor }) => $titleColor};

    font-size: 15px;
    font-weight: 900;
  }

  p {
    margin: 5px 0;

    color: ${({ $textColor }) => $textColor};

    font-size: 14px;

    line-height: 1.5;

    overflow-wrap: anywhere;
  }

  @media (max-width: 550px) {
    text-align: center;
  }
`;

const FooterLogo = styled.h2`
  margin: 0 0 15px;

  color: ${({ $titleColor }) => $titleColor};

  font-size: 24px;
  font-weight: 900;

  overflow-wrap: anywhere;

  span {
    color: ${({ $accentColor }) => $accentColor};
  }
`;

const ContactRow = styled.div`
  min-width: 0;

  display: flex;
  align-items: center;

  gap: 12px;

  margin-bottom: 10px;

  color: ${({ $textColor }) => $textColor};

  font-size: 14px;
  line-height: 1.5;

  svg {
    color: ${({ $iconColor }) => $iconColor};

    flex-shrink: 0;
  }

  span {
    min-width: 0;

    overflow-wrap: anywhere;
  }

  @media (max-width: 550px) {
    justify-content: center;
  }
`;

const ContactLink = styled.a`
  min-width: 0;

  color: ${({ $linkColor }) => $linkColor};

  text-decoration: none;

  overflow-wrap: anywhere;

  &:hover {
    text-decoration: underline;
  }

  &:focus-visible {
    outline: 2px solid ${({ $focusColor }) => $focusColor};
    outline-offset: 3px;

    border-radius: 2px;
  }
`;

const Socials = styled.div`
  display: flex;
  flex-wrap: wrap;

  gap: 12px;

  margin-top: 20px;

  @media (max-width: 550px) {
    justify-content: center;
  }
`;

const SocialLink = styled.a`
  width: 35px;
  height: 35px;

  border-radius: 50%;

  display: flex;
  align-items: center;
  justify-content: center;

  background: ${({ $backgroundColor }) => $backgroundColor};

  color: ${({ $iconColor }) => $iconColor};

  text-decoration: none;

  transition:
    transform 0.25s ease,
    background 0.25s ease,
    color 0.25s ease;

  &:hover {
    transform: translateY(-3px);

    background: ${({ $accentColor }) => $accentColor};

    color: ${({ $activeIconColor }) => $activeIconColor};
  }

  &:focus-visible {
    outline: 3px solid ${({ $focusColor }) => $focusColor};
    outline-offset: 3px;

    background: ${({ $accentColor }) => $accentColor};

    color: ${({ $activeIconColor }) => $activeIconColor};
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;

    &:hover {
      transform: none;
    }
  }
`;

const FooterBottom = styled.div`
  margin-top: 30px;
  padding-top: 18px;

  border-top: 1px solid ${({ $separatorColor }) => $separatorColor};

  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;

  gap: 15px;

  color: ${({ $textColor }) => $textColor};

  font-size: 12px;

  @media (max-width: 550px) {
    flex-direction: column;
    align-items: center;
    justify-content: center;

    text-align: center;
  }
`;

const FooterBottomRight = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;

  gap: 8px;

  @media (max-width: 550px) {
    align-items: center;
  }
`;

const FooterLegal = styled.nav`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;

  gap: 8px;

  @media (max-width: 550px) {
    justify-content: center;
  }
`;

const FooterLegalLink = styled.a`
  color: ${({ $linkColor }) => $linkColor};

  text-decoration: none;

  overflow-wrap: anywhere;

  &:hover {
    text-decoration: underline;
  }

  &:focus-visible {
    outline: 2px solid ${({ $focusColor }) => $focusColor};
    outline-offset: 3px;

    border-radius: 2px;
  }
`;

const FooterLegalButton = styled.button`
  padding: 0;

  border: 0;

  background: transparent;

  color: ${({ $linkColor }) => $linkColor};

  font: inherit;

  text-decoration: none;

  overflow-wrap: anywhere;

  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }

  &:focus-visible {
    outline: 2px solid ${({ $focusColor }) => $focusColor};
    outline-offset: 3px;

    border-radius: 2px;
  }
`;

const FooterLegalSeparator = styled.span`
  color: ${({ $separatorColor }) => $separatorColor};
`;

const FooterDeveloper = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;

  gap: 4px;

  @media (max-width: 550px) {
    justify-content: center;
  }
`;

const FooterDeveloperLink = styled.a`
  color: ${({ $linkColor }) => $linkColor};

  font-weight: 700;

  text-decoration: none;

  overflow-wrap: anywhere;

  &:hover {
    text-decoration: underline;
  }

  &:focus-visible {
    outline: 2px solid ${({ $focusColor }) => $focusColor};
    outline-offset: 3px;

    border-radius: 2px;
  }
`;

function normalizeText(value, fallback) {
  if (typeof value !== 'string') {
    return fallback;
  }

  const normalizedValue = value.trim();

  return normalizedValue || fallback;
}

function normalizeOptionalText(value, fallback) {
  if (value === undefined || value === null) {
    return fallback;
  }

  if (typeof value !== 'string') {
    return '';
  }

  return value.trim();
}

function normalizeBoolean(value, fallback) {
  return typeof value === 'boolean' ? value : fallback;
}

function normalizeColor(value, fallback) {
  if (typeof value !== 'string') {
    return fallback;
  }

  const normalizedValue = value.trim();

  return HEX_COLOR_PATTERN.test(normalizedValue) ? normalizedValue : fallback;
}

function createParagraphs(value) {
  return value
    .split(/\n\s*\n/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean);
}

function replaceCurrentYear(value) {
  const currentYear = new Date().getFullYear();

  return value.replace(/\{year\}/g, String(currentYear));
}

function isExternalUrl(value) {
  return /^https?:\/\//i.test(value);
}

function normalizeLegalHref(value, fallbackHash) {
  const normalizedValue = normalizeOptionalText(value, '');

  if (!normalizedValue || normalizedValue === '#') {
    return fallbackHash;
  }

  return normalizedValue;
}

function createPhoneHref(value) {
  if (typeof value !== 'string') {
    return '';
  }

  const normalizedValue = value.trim();

  if (!normalizedValue) {
    return '';
  }

  const digits = normalizedValue.replace(/\D/g, '');

  if (!digits) {
    return '';
  }

  const prefix = normalizedValue.startsWith('+') ? '+' : '';

  return `tel:${prefix}${digits}`;
}

function normalizeSocialLinks(footerSettings) {
  const sourceSocialLinks = footerSettings ? footerSettings.social_links : DEFAULT_SOCIAL_LINKS;

  if (!Array.isArray(sourceSocialLinks)) {
    return [];
  }

  return sourceSocialLinks
    .map((socialLink) => {
      if (!socialLink || typeof socialLink !== 'object') {
        return null;
      }

      const icon = typeof socialLink.icon === 'string' ? socialLink.icon.trim() : '';

      const url = typeof socialLink.url === 'string' ? socialLink.url.trim() : '';

      const name = typeof socialLink.name === 'string' ? socialLink.name.trim() : '';

      if (!icon || !url || !SOCIAL_ICON_COMPONENTS[icon]) {
        return null;
      }

      return {
        id: socialLink.id,
        icon,
        url,
        label: name || SOCIAL_ICON_LABELS[icon],
      };
    })
    .filter(Boolean);
}

function getAvailableLegalDocumentTypes(legalSettings) {
  if (!legalSettings || typeof legalSettings !== 'object') {
    return null;
  }

  if (!Array.isArray(legalSettings.documents)) {
    return new Set();
  }

  return new Set(
    legalSettings.documents
      .map((document) => {
        if (!document || typeof document !== 'object') {
          return null;
        }

        return typeof document.document_type === 'string' ? document.document_type : null;
      })
      .filter(Boolean),
  );
}

function Footer({ footerSettings, legalSettings, onManageCookies }) {
  const backgroundColor = normalizeColor(
    footerSettings?.background_color,
    DEFAULT_BACKGROUND_COLOR,
  );

  const textColor = normalizeColor(footerSettings?.text_color, DEFAULT_TEXT_COLOR);

  const titleColor = normalizeColor(footerSettings?.title_color, DEFAULT_TITLE_COLOR);

  const accentColor = normalizeColor(footerSettings?.accent_color, DEFAULT_ACCENT_COLOR);

  const linkColor = normalizeColor(footerSettings?.link_color, DEFAULT_LINK_COLOR);

  const separatorColor = normalizeColor(footerSettings?.separator_color, DEFAULT_SEPARATOR_COLOR);

  const brandPrimaryText = normalizeText(
    footerSettings?.brand_primary_text,
    DEFAULT_BRAND_PRIMARY_TEXT,
  );

  const brandAccentText = normalizeText(
    footerSettings?.brand_accent_text,
    DEFAULT_BRAND_ACCENT_TEXT,
  );

  const description = normalizeText(footerSettings?.description, DEFAULT_DESCRIPTION);

  const hoursTitle = normalizeText(footerSettings?.hours_title, DEFAULT_HOURS_TITLE);

  const hoursText = normalizeText(footerSettings?.hours_text, DEFAULT_HOURS_TEXT);

  const contactsTitle = normalizeText(footerSettings?.contacts_title, DEFAULT_CONTACTS_TITLE);

  const contactPhone = normalizeOptionalText(footerSettings?.contact_phone, DEFAULT_CONTACT_PHONE);

  const contactAddress = normalizeOptionalText(
    footerSettings?.contact_address,
    DEFAULT_CONTACT_ADDRESS,
  );

  const contactEmail = normalizeOptionalText(footerSettings?.contact_email, DEFAULT_CONTACT_EMAIL);

  const vatTitle = normalizeText(footerSettings?.vat_title, DEFAULT_VAT_TITLE);

  const vatNumber = normalizeOptionalText(footerSettings?.vat_number, DEFAULT_VAT_NUMBER);

  const copyrightTemplate = normalizeText(footerSettings?.copyright_text, DEFAULT_COPYRIGHT_TEXT);

  const privacyLabel = normalizeOptionalText(footerSettings?.privacy_label, DEFAULT_PRIVACY_LABEL);

  const privacyHref = normalizeLegalHref(footerSettings?.privacy_url, DEFAULT_PRIVACY_URL);

  const showPrivacy = normalizeBoolean(footerSettings?.show_privacy, DEFAULT_SHOW_PRIVACY);

  const termsLabel = normalizeOptionalText(footerSettings?.terms_label, DEFAULT_TERMS_LABEL);

  const termsHref = normalizeLegalHref(footerSettings?.terms_url, DEFAULT_TERMS_URL);

  const showTerms = normalizeBoolean(footerSettings?.show_terms, DEFAULT_SHOW_TERMS);

  const cookiePolicyLabel = normalizeOptionalText(
    legalSettings?.cookie_policy_link_label,
    DEFAULT_COOKIE_POLICY_LABEL,
  );

  const manageCookiesLabel = normalizeOptionalText(
    legalSettings?.manage_cookies_label,
    DEFAULT_MANAGE_COOKIES_LABEL,
  );

  const showManageCookies = normalizeBoolean(
    legalSettings?.show_manage_cookies,
    DEFAULT_SHOW_MANAGE_COOKIES,
  );

  const cookieBannerIsActive = normalizeBoolean(
    legalSettings?.is_cookie_banner_active,
    DEFAULT_COOKIE_BANNER_ACTIVE,
  );

  const developerText = normalizeOptionalText(
    footerSettings?.developer_text,
    DEFAULT_DEVELOPER_TEXT,
  );

  const developerName = normalizeOptionalText(
    footerSettings?.developer_name,
    DEFAULT_DEVELOPER_NAME,
  );

  const developerUrl = normalizeOptionalText(footerSettings?.developer_url, DEFAULT_DEVELOPER_URL);

  const copyrightText = replaceCurrentYear(copyrightTemplate);

  const descriptionParagraphs = createParagraphs(description);

  const hoursParagraphs = createParagraphs(hoursText);

  const socialLinks = normalizeSocialLinks(footerSettings);

  const contactPhoneHref = createPhoneHref(contactPhone);

  const availableLegalDocumentTypes = getAvailableLegalDocumentTypes(legalSettings);

  const documentAvailabilityIsControlled = availableLegalDocumentTypes instanceof Set;

  const privacyDocumentIsAvailable =
    !documentAvailabilityIsControlled ||
    availableLegalDocumentTypes.has(LEGAL_DOCUMENT_TYPES.privacy);

  const termsDocumentIsAvailable =
    !documentAvailabilityIsControlled ||
    availableLegalDocumentTypes.has(LEGAL_DOCUMENT_TYPES.terms);

  const cookiePolicyDocumentIsAvailable =
    !documentAvailabilityIsControlled ||
    availableLegalDocumentTypes.has(LEGAL_DOCUMENT_TYPES.cookiePolicy);

  const shouldShowPhone = Boolean(contactPhone) && Boolean(contactPhoneHref);

  const shouldShowAddress = Boolean(contactAddress);
  const shouldShowEmail = Boolean(contactEmail);
  const shouldShowVat = Boolean(vatNumber);

  const shouldShowPrivacy =
    showPrivacy && privacyDocumentIsAvailable && Boolean(privacyLabel) && Boolean(privacyHref);

  const shouldShowTerms =
    showTerms && termsDocumentIsAvailable && Boolean(termsLabel) && Boolean(termsHref);

  const shouldShowCookiePolicy = cookiePolicyDocumentIsAvailable && Boolean(cookiePolicyLabel);

  const shouldShowManageCookies =
    cookieBannerIsActive &&
    showManageCookies &&
    Boolean(manageCookiesLabel) &&
    typeof onManageCookies === 'function';

  const legalItems = [
    shouldShowPrivacy
      ? {
          key: 'privacy',
          label: privacyLabel,
          href: privacyHref,
          type: 'link',
        }
      : null,

    shouldShowTerms
      ? {
          key: 'terms',
          label: termsLabel,
          href: termsHref,
          type: 'link',
        }
      : null,

    shouldShowCookiePolicy
      ? {
          key: 'cookie-policy',
          label: cookiePolicyLabel,
          href: COOKIE_POLICY_HASH,
          type: 'link',
        }
      : null,

    shouldShowManageCookies
      ? {
          key: 'manage-cookies',
          label: manageCookiesLabel,
          type: 'button',
        }
      : null,
  ].filter(Boolean);

  const shouldShowLegalLinks = legalItems.length > 0;

  const shouldShowDeveloper =
    Boolean(developerText) && Boolean(developerName) && Boolean(developerUrl);

  const shouldShowFooterRight = shouldShowLegalLinks || shouldShowDeveloper;

  const developerIsExternal = isExternalUrl(developerUrl);

  return (
    <FooterWrapper id="contatti" $backgroundColor={backgroundColor} $textColor={textColor}>
      <FooterContainer>
        <FooterGrid>
          <FooterColumn $titleColor={titleColor} $textColor={textColor}>
            <FooterLogo $titleColor={titleColor} $accentColor={accentColor}>
              {brandPrimaryText} <span>{brandAccentText}</span>
            </FooterLogo>

            {descriptionParagraphs.map((paragraph, index) => (
              <p key={`description-${index}`}>{paragraph}</p>
            ))}

            {socialLinks.length > 0 && (
              <Socials aria-label="Social network">
                {socialLinks.map((socialLink, index) => {
                  const IconComponent = SOCIAL_ICON_COMPONENTS[socialLink.icon];

                  const socialIsExternal = isExternalUrl(socialLink.url);

                  return (
                    <SocialLink
                      key={socialLink.id ?? `${socialLink.icon}-${index}`}
                      href={socialLink.url}
                      aria-label={socialLink.label}
                      title={socialLink.label}
                      target={socialIsExternal ? '_blank' : undefined}
                      rel={socialIsExternal ? 'noopener noreferrer' : undefined}
                      $backgroundColor={linkColor}
                      $iconColor={backgroundColor}
                      $accentColor={accentColor}
                      $activeIconColor={titleColor}
                      $focusColor={linkColor}
                    >
                      <IconComponent aria-hidden="true" focusable="false" />
                    </SocialLink>
                  );
                })}
              </Socials>
            )}
          </FooterColumn>

          <FooterColumn $titleColor={titleColor} $textColor={textColor}>
            <h3>{hoursTitle}</h3>

            {hoursParagraphs.map((paragraph, index) => (
              <p key={`hours-${index}`}>{paragraph}</p>
            ))}
          </FooterColumn>

          <FooterColumn $titleColor={titleColor} $textColor={textColor}>
            <h3>{contactsTitle}</h3>

            {shouldShowPhone && (
              <ContactRow $textColor={textColor} $iconColor={titleColor}>
                <FaPhone aria-hidden="true" focusable="false" />

                <ContactLink
                  href={contactPhoneHref}
                  $linkColor={linkColor}
                  $focusColor={accentColor}
                >
                  {contactPhone}
                </ContactLink>
              </ContactRow>
            )}

            {shouldShowAddress && (
              <ContactRow $textColor={textColor} $iconColor={titleColor}>
                <FaLocationDot aria-hidden="true" focusable="false" />

                <span>{contactAddress}</span>
              </ContactRow>
            )}

            {shouldShowEmail && (
              <ContactRow $textColor={textColor} $iconColor={titleColor}>
                <FaEnvelope aria-hidden="true" focusable="false" />

                <ContactLink
                  href={`mailto:${contactEmail}`}
                  $linkColor={linkColor}
                  $focusColor={accentColor}
                >
                  {contactEmail}
                </ContactLink>
              </ContactRow>
            )}
          </FooterColumn>

          {shouldShowVat && (
            <FooterColumn $titleColor={titleColor} $textColor={textColor}>
              <h3>{vatTitle}</h3>

              <p>{vatNumber}</p>
            </FooterColumn>
          )}
        </FooterGrid>

        <FooterBottom $textColor={textColor} $separatorColor={separatorColor}>
          <span>{copyrightText}</span>

          {shouldShowFooterRight && (
            <FooterBottomRight>
              {shouldShowLegalLinks && (
                <FooterLegal aria-label="Informazioni legali">
                  {legalItems.map((legalItem, index) => (
                    <React.Fragment key={legalItem.key}>
                      {index > 0 && (
                        <FooterLegalSeparator aria-hidden="true" $separatorColor={separatorColor}>
                          |
                        </FooterLegalSeparator>
                      )}

                      {legalItem.type === 'button' ? (
                        <FooterLegalButton
                          type="button"
                          onClick={onManageCookies}
                          $linkColor={linkColor}
                          $focusColor={accentColor}
                        >
                          {legalItem.label}
                        </FooterLegalButton>
                      ) : (
                        <FooterLegalLink
                          href={legalItem.href}
                          $linkColor={linkColor}
                          $focusColor={accentColor}
                        >
                          {legalItem.label}
                        </FooterLegalLink>
                      )}
                    </React.Fragment>
                  ))}
                </FooterLegal>
              )}

              {shouldShowDeveloper && (
                <FooterDeveloper>
                  <span>{developerText}</span>

                  <FooterDeveloperLink
                    href={developerUrl}
                    target={developerIsExternal ? '_blank' : undefined}
                    rel={developerIsExternal ? 'noopener noreferrer' : undefined}
                    $linkColor={linkColor}
                    $focusColor={accentColor}
                  >
                    {developerName}
                  </FooterDeveloperLink>
                </FooterDeveloper>
              )}
            </FooterBottomRight>
          )}
        </FooterBottom>
      </FooterContainer>
    </FooterWrapper>
  );
}

export default Footer;
